import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export type BriefStatus =
  | "not_required"
  | "not_started"
  | "in_progress"
  | "submitted"
  | "approved"
  | "declined";

export interface Brief {
  project_id: string;
  status: BriefStatus;

  requester_name: string | null;
  requester_department: string | null;
  primary_contact: string | null;

  expected_capital: number | null;
  expected_other: number | null;
  budgeted_capital: number | null;
  budgeted_other: number | null;

  business_need: string | null;
  strategic_anchor: string | null;
  target_kpi: string | null;

  in_scope: string | null;
  out_of_scope: string | null;
  success_criteria: string | null;

  business_owner: string | null;
  key_departments: string | null;

  expected_start: string | null;
  key_milestones: string | null;

  top_risks: string | null;
  dependencies: string | null;

  it_needed: string | null;
  tshirt_size: string | null;
  cost_range: string | null;

  it_checklist: string[];

  other_info: string | null;
  intake_discussion_dates: string | null;
  decline_reason: string | null;

  created_at: string | null;
  updated_at: string | null;
  submitted_at: string | null;
  decided_at: string | null;
  decided_by: string | null;
}

export type BriefPatch = Partial<
  Omit<
    Brief,
    | "project_id"
    | "created_at"
    | "updated_at"
    | "submitted_at"
    | "decided_at"
    | "decided_by"
    | "decline_reason"
  >
>;

export const briefKeys = {
  detail: (projectId: string) => ["brief", projectId] as const,
};

export function useBrief(projectId: string | null) {
  return useQuery<Brief>({
    queryKey: briefKeys.detail(projectId ?? ""),
    queryFn: () => api.get(`/briefs/${projectId}`).then((r) => r.data),
    enabled: Boolean(projectId),
  });
}

export function useUpsertBrief(projectId: string) {
  const qc = useQueryClient();
  return useMutation<Brief, Error, BriefPatch>({
    mutationFn: (patch) =>
      api.put(`/briefs/${projectId}`, patch).then((r) => r.data),
    onSuccess: (data) => {
      qc.setQueryData(briefKeys.detail(projectId), data);
      // Portfolio table renders brief_status — keep it fresh so the
      // "Brief Received" pill updates immediately after a toggle.
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}

export interface BriefActionPayload {
  decided_by?: string;
  reason?: string;
  required?: boolean;
}

export function useBriefAction(projectId: string) {
  const qc = useQueryClient();
  return useMutation<
    Brief,
    Error,
    { action: "submit" | "approve" | "decline" | "reopen" | "require"; payload?: BriefActionPayload }
  >({
    mutationFn: ({ action, payload }) => {
      const body = payload ?? {};
      return api.post(`/briefs/${projectId}/${action}`, body).then((r) => r.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(briefKeys.detail(projectId), data);
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}
